"use client"

import type React from "react"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Star,
  Waves,
  Wind,
  Shield,
  Anchor,
  Leaf,
  Clock,
  DollarSign,
  Wifi,
  Sun,
  Utensils,
  Navigation,
  Users,
  Car,
  TrendingUp,
  Sparkles,
} from "lucide-react"
import { useState, useEffect } from "react"
import { createClient } from "@/lib/supabase/client"
import { useAuth } from "@/lib/auth/context"
import { useProfile } from "@/hooks/use-profile"
import { toast } from "sonner"
import { UnsplashImagePicker } from "@/components/unsplash-image-picker"
import { usePlatform, triggerHaptic } from "@/hooks/use-platform"
import { filterProfanity } from "@/lib/utils/profanity-filter"

interface EnhancedReviewFormProps {
  surfSpotId: string
  onReviewAdded?: () => void
  onCancel?: () => void
  reviewId?: string
  spotLocation?: string
  defaultActivityType?: "surfing" | "kitesurfing" // Pre-select activity type from URL
  isMobile?: boolean
  initialData?: {
    overall_rating: number
    wave_quality: number
    crowd_level: number
    water_quality: number
    amenities_rating: number
    comment: string | null
    activity_type: "surfing" | "kitesurfing"
    wind_conditions: string[] | null
    wave_conditions: string[] | null
    water_conditions: string[] | null
    safety_features: string[] | null
    facilities: string[] | null
    accessibility: string[] | null
    wave_type: string
    skill_level: string
    best_wind_direction: string
    best_swell_direction: string
  }
}

export function EnhancedReviewForm({
  surfSpotId,
  onReviewAdded,
  onCancel,
  reviewId,
  spotLocation,
  defaultActivityType,
  initialData,
}: EnhancedReviewFormProps) {
  const { user } = useAuth()
  const { profile } = useProfile()
  const [submitting, setSubmitting] = useState(false)
  const [activityType, setActivityType] = useState<"surfing" | "kitesurfing" | null>(defaultActivityType || null)
  const [overallRating, setOverallRating] = useState(0)
  const [ratings, setRatings] = useState({
    wave_quality: 0,
    crowd_level: 0,
    water_quality: 0,
    amenities_rating: 0,
  })
  const [comment, setComment] = useState("")

  // Filter selections
  const [selectedFilters, setSelectedFilters] = useState<string[]>([])

  const [coreAttributes, setCoreAttributes] = useState({
    wave_type: "",
    skill_level: "",
    best_wind_direction: "",
    best_swell_direction: "",
  })

  const [selectedImage, setSelectedImage] = useState<{
    unsplashId: string
    url: string
    thumbUrl: string
    photographerName: string
    photographerUsername: string
    photographerUrl: string
  } | null>(null)
  const [imagePickerRefreshTrigger, setImagePickerRefreshTrigger] = useState(0)

  useEffect(() => {
    if (initialData) {
      setActivityType(initialData.activity_type)
      setOverallRating(initialData.overall_rating)
      setRatings({
        wave_quality: initialData.wave_quality,
        crowd_level: initialData.crowd_level,
        water_quality: initialData.water_quality,
        amenities_rating: initialData.amenities_rating,
      })
      setComment(initialData.comment || "")

      const allFilters = [
        ...(initialData.wind_conditions || []),
        ...(initialData.wave_conditions || []),
        ...(initialData.water_conditions || []),
        ...(initialData.safety_features || []),
        ...(initialData.facilities || []),
        ...(initialData.accessibility || []),
      ]
      setSelectedFilters(allFilters)

      setCoreAttributes({
        wave_type: initialData.wave_type || "",
        skill_level: initialData.skill_level || "",
        best_wind_direction: initialData.best_wind_direction || "",
        best_swell_direction: initialData.best_swell_direction || "",
      })
    }
  }, [initialData])

  const activityTypes = [
    { label: "Surfing", emoji: "🏄‍♂️", value: "surfing" as const },
    { label: "Kitesurfing", emoji: "🪁", value: "kitesurfing" as const },
  ]

  const coreAttributeFilters = [
    {
      category: "Break Type",
      icon: Waves,
      field: "wave_type",
      activityTypes: ["surfing"],
      options: [
        { label: "Beach Break", emoji: "🏖️", value: "beach_break" },
        { label: "Reef Break", emoji: "🪸", value: "reef_break" },
        { label: "Point Break", emoji: "🌊", value: "point_break" },
        { label: "River Mouth", emoji: "🏞️", value: "river_mouth" },
      ],
    },
    {
      category: "Skill Level",
      icon: TrendingUp,
      field: "skill_level",
      activityTypes: ["surfing", "kitesurfing"],
      options: [
        { label: "Beginner", emoji: "🟢", value: "beginner" },
        { label: "Intermediate", emoji: "🟡", value: "intermediate" },
        { label: "Advanced", emoji: "🟠", value: "advanced" },
        { label: "Expert", emoji: "🔴", value: "expert" },
      ],
    },
    {
      category: "Best Swell Direction",
      icon: Navigation,
      field: "best_swell_direction",
      activityTypes: ["surfing"],
      options: [
        { label: "North", emoji: "⬆️", value: "N" },
        { label: "Northeast", emoji: "↗️", value: "NE" },
        { label: "East", emoji: "➡️", value: "E" },
        { label: "Southeast", emoji: "↘️", value: "SE" },
        { label: "South", emoji: "⬇️", value: "S" },
        { label: "Southwest", emoji: "↙️", value: "SW" },
        { label: "West", emoji: "⬅️", value: "W" },
        { label: "Northwest", emoji: "↖️", value: "NW" },
      ],
    },
    {
      category: "Best Wind Direction",
      icon: Wind,
      field: "best_wind_direction",
      activityTypes: ["surfing", "kitesurfing"],
      options: [
        { label: "North", emoji: "⬆️", value: "N" },
        { label: "Northeast", emoji: "↗️", value: "NE" },
        { label: "East", emoji: "➡️", value: "E" },
        { label: "Southeast", emoji: "↘️", value: "SE" },
        { label: "South", emoji: "⬇️", value: "S" },
        { label: "Southwest", emoji: "↙️", value: "SW" },
        { label: "West", emoji: "⬅️", value: "W" },
        { label: "Northwest", emoji: "↖️", value: "NW" },
      ],
    },
  ]

  const additionalCharacteristics = [
    {
      category: "Best Tide",
      icon: Waves,
      activityTypes: ["surfing"],
      options: [
        { label: "Low Tide", emoji: "🌊", value: "low_tide" },
        { label: "Mid Tide", emoji: "🌊", value: "mid_tide" },
        { label: "High Tide", emoji: "🌊", value: "high_tide" },
        { label: "All Tides", emoji: "🌊", value: "all_tides" },
      ],
    },
    {
      category: "Crowd Level",
      icon: Users,
      activityTypes: ["surfing", "kitesurfing"],
      options: [
        { label: "Uncrowded", emoji: "😌", value: "uncrowded" },
        { label: "Moderate", emoji: "👥", value: "moderate_crowd" },
        { label: "Crowded", emoji: "👨‍👩‍👧‍👦", value: "crowded" },
      ],
    },
    {
      category: "Parking",
      icon: Car,
      activityTypes: ["surfing", "kitesurfing"],
      options: [
        { label: "Free Parking", emoji: "🅿️", value: "free_parking" },
        { label: "Paid Parking", emoji: "💰", value: "paid_parking" },
        { label: "Street Parking", emoji: "🚗", value: "street_parking" },
        { label: "No Parking", emoji: "🚫", value: "no_parking" },
      ],
    },
    {
      category: "Launch Type",
      icon: Anchor,
      activityTypes: ["kitesurfing"],
      options: [
        { label: "Beach Launch", emoji: "🏖️", value: "beach_launch" },
        { label: "Boat Launch", emoji: "⛵", value: "boat_launch" },
        { label: "Shore Break", emoji: "🌊", value: "shore_break" },
      ],
    },
  ]

  const surfingFilters = [
    {
      category: "Wave Conditions",
      icon: Waves,
      options: [
        { label: "Small waves", emoji: "🌊", value: "small_waves" },
        { label: "Medium waves", emoji: "🌊", value: "medium_waves" },
        { label: "Big waves", emoji: "🌊", value: "big_waves" },
      ],
    },
    {
      category: "Sea Floor Safety",
      icon: Anchor,
      options: [
        { label: "Safe sandy bottom", emoji: "🏖️", value: "safe_sandy" },
        { label: "Rocky bottom", emoji: "🪨", value: "rocky_bottom" },
        { label: "Shallow reef", emoji: "🪸", value: "shallow_reef" },
        { label: "Sharp reef danger", emoji: "⚠️", value: "sharp_reef" },
      ],
    },
  ]

  const kitesurfingFilters = [
    {
      category: "Wind Conditions",
      icon: Wind,
      options: [
        { label: "15+ knots", emoji: "💨", value: "strong_wind" },
        { label: "Offshore winds", emoji: "🌬️", value: "offshore" },
        { label: "Side shore", emoji: "↔️", value: "side_shore" },
        { label: "Light winds", emoji: "🍃", value: "light_winds" },
        { label: "Consistent wind", emoji: "🌪️", value: "consistent_wind" },
      ],
    },
    {
      category: "Water Conditions",
      icon: Waves,
      options: [
        { label: "Flat water", emoji: "🏞️", value: "flat_water" },
        { label: "Choppy water", emoji: "🌊", value: "choppy_water" },
        { label: "Warm water", emoji: "🌡️", value: "warm_water" },
        { label: "Cold water", emoji: "🧊", value: "cold_water" },
        { label: "Deep water", emoji: "🏊‍♂️", value: "deep_water" },
        { label: "Shallow water", emoji: "🦶", value: "shallow_water" },
      ],
    },
  ]

  const universalFilters = [
    {
      category: "Safety",
      icon: Shield,
      options: [
        { label: "Safe launch", emoji: "🛟", value: "safe_launch" },
        { label: "Lifeguards", emoji: "👨‍⚕️", value: "lifeguards" },
        { label: "No hazards", emoji: "✅", value: "no_hazards" },
        { label: "Safe for kids", emoji: "👶", value: "safe_kids" },
        { label: "Good visibility", emoji: "👁️", value: "good_visibility" },
      ],
    },
    {
      category: "Cleanliness",
      icon: Leaf,
      options: [
        { label: "Pristine", emoji: "✨", value: "pristine" },
        { label: "Very clean", emoji: "🌿", value: "very_clean" },
        { label: "Moderately clean", emoji: "🌱", value: "moderate_clean" },
      ],
    },
    {
      category: "Season",
      icon: Clock,
      options: [
        { label: "Year-round", emoji: "🗓️", value: "year_round" },
        { label: "Summer season", emoji: "☀️", value: "summer" },
        { label: "Winter season", emoji: "❄️", value: "winter" },
        { label: "Windy season", emoji: "🌬️", value: "windy_season" },
      ],
    },
    {
      category: "Cost",
      icon: DollarSign,
      options: [
        { label: "<$10/day", emoji: "💚", value: "budget" },
        { label: "<$25/day", emoji: "💛", value: "moderate" },
        { label: "<$50/day", emoji: "🧡", value: "expensive" },
      ],
    },
    {
      category: "Amenities",
      icon: Wifi,
      options: [
        { label: "WiFi nearby", emoji: "📶", value: "wifi" },
        { label: "Surf schools", emoji: "🏫", value: "surf_schools" },
        { label: "Easy access", emoji: "🚗", value: "easy_access" },
        { label: "Gear rental", emoji: "🏄‍♂️", value: "gear_rental" },
        { label: "Repair shops", emoji: "🔧", value: "repair_shops" },
      ],
    },
    {
      category: "Climate",
      icon: Sun,
      options: [
        { label: "Tropical", emoji: "🌴", value: "tropical" },
        { label: "Warm year-round", emoji: "☀️", value: "warm_climate" },
        { label: "Mild temperatures", emoji: "⛅", value: "mild" },
        { label: "Cool climate", emoji: "🌡️", value: "cool" },
      ],
    },
    {
      category: "Food & Dining",
      icon: Utensils,
      options: [
        { label: "Great restaurants", emoji: "🍽️", value: "restaurants" },
        { label: "Beach cafes", emoji: "☕", value: "cafes" },
        { label: "Local seafood", emoji: "🦐", value: "seafood" },
        { label: "Vegetarian options", emoji: "🥗", value: "vegetarian" },
      ],
    },
  ]

  const handleRatingChange = (category: keyof typeof ratings, rating: number) => {
    triggerHaptic('impact')
    setRatings((prev) => ({ ...prev, [category]: rating }))
  }

  const handleOverallRatingChange = (rating: number) => {
    triggerHaptic('selection')
    setOverallRating(rating)
  }

  const handleFilterToggle = (filterValue: string) => {
    triggerHaptic('impact')
    setSelectedFilters((prev) =>
      prev.includes(filterValue) ? prev.filter((f) => f !== filterValue) : [...prev, filterValue],
    )
  }

  const handleCoreAttributeSelect = (field: string, value: string) => {
    triggerHaptic('impact')
    setCoreAttributes((prev) => ({
      ...prev,
      [field]: prev[field as keyof typeof prev] === value ? "" : value,
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    triggerHaptic('selection')

    if (!user) {
      toast.error("You must be logged in to write a review")
      return
    }

    if (!profile?.id) {
      toast.error("User profile not found. Please try logging in again.")
      return
    }

    if (!activityType) {
      toast.error("Please select your activity type")
      return
    }

    if (overallRating === 0) {
      toast.error("Please provide an overall rating")
      return
    }

    setSubmitting(true)
    const supabase = createClient()

    if (!supabase) {
      toast.error("Failed to connect to the database")
      setSubmitting(false)
      return
    }

    try {
      const waveConditions = selectedFilters.filter((f) =>
        [
          "small_waves",
          "medium_waves",
          "big_waves",
          "safe_sandy",
          "rocky_bottom",
          "shallow_reef",
          "sharp_reef",
        ].includes(f),
      )
      const windConditions = selectedFilters.filter((f) =>
        ["strong_wind", "offshore", "side_shore", "light_winds", "consistent_wind"].includes(f),
      )
      const waterConditions = selectedFilters.filter((f) =>
        [
          "flat_water",
          "choppy_water",
          "warm_water",
          "cold_water",
          "deep_water",
          "shallow_water",
          "pristine",
          "very_clean",
          "moderate_clean",
        ].includes(f),
      )
      const safetyFeatures = selectedFilters.filter((f) =>
        ["safe_launch", "lifeguards", "no_hazards", "not_crowded", "safe_kids", "good_visibility"].includes(f),
      )
      const facilities = selectedFilters.filter((f) =>
        [
          "wifi",
          "surf_schools",
          "easy_access",
          "gear_rental",
          "repair_shops",
          "restaurants",
          "cafes",
          "seafood",
          "vegetarian",
        ].includes(f),
      )
      const accessibility = selectedFilters.filter((f) =>
        [
          "year_round",
          "summer",
          "winter",
          "windy_season",
          "budget",
          "moderate",
          "expensive",
          "tropical",
          "warm_climate",
          "mild",
          "cool",
        ].includes(f),
      )

      // Apply profanity filter to comment
      const filteredComment = comment ? filterProfanity(comment.trim()) : null

      const reviewData = {
        user_id: profile.id,
        surf_spot_id: surfSpotId,
        overall_rating: overallRating,
        wave_quality: ratings.wave_quality || overallRating,
        crowd_level: ratings.crowd_level || overallRating,
        water_quality: ratings.water_quality || overallRating,
        amenities_rating: ratings.amenities_rating || overallRating,
        comment: filteredComment,
        activity_type: activityType,
        wind_conditions: windConditions.length > 0 ? windConditions : null,
        wave_conditions: waveConditions.length > 0 ? waveConditions : null,
        water_conditions: waterConditions.length > 0 ? waterConditions : null,
        safety_features: safetyFeatures.length > 0 ? safetyFeatures : null,
        facilities: facilities.length > 0 ? facilities : null,
        accessibility: accessibility.length > 0 ? accessibility : null,
        wave_type: coreAttributes.wave_type || null,
        skill_level: coreAttributes.skill_level || null,
        best_wind_direction: coreAttributes.best_wind_direction || null,
        best_swell_direction: coreAttributes.best_swell_direction || null,
      }

      let error
      if (reviewId) {
        const result = await supabase.from("reviews").update(reviewData).eq("id", reviewId)
        error = result.error
      } else {
        const result = await supabase.from("reviews").insert(reviewData)
        error = result.error
      }

      if (error) {
        console.error("Error submitting review:", error)
        toast.error("Failed to submit review")
        return
      }

      if (selectedImage && !reviewId) {
        // Insert the image with 1 vote (the person adding it automatically votes for it)
        const { data: insertedImage, error: imageError } = await supabase
          .from("spot_images")
          .insert({
            spot_id: surfSpotId,
            unsplash_id: selectedImage.unsplashId,
            unsplash_url: selectedImage.url,
            unsplash_thumb_url: selectedImage.thumbUrl,
            photographer_name: selectedImage.photographerName,
            photographer_username: selectedImage.photographerUsername,
            photographer_url: selectedImage.photographerUrl,
            added_by: profile.id,
            votes: 1, // Start with 1 vote since the person adding it votes for it
          })
          .select("id")
          .single()

        if (imageError && imageError.code !== "23505") {
          console.error("Error saving image:", imageError)
        } else if (insertedImage?.id) {
          // Also add a vote record for the person who added the image
          await supabase.from("spot_image_votes").insert({
            image_id: insertedImage.id,
            user_id: profile.id,
          })
          
          // Trigger refresh of image picker to show updated vote count
          setImagePickerRefreshTrigger(prev => prev + 1)
        }
      }

      toast.success(reviewId ? "Review updated successfully!" : "Review submitted successfully!")
      if (onReviewAdded) {
        onReviewAdded()
      } else {
        window.location.href = "/dashboard"
      }
    } catch (error) {
      console.error("Error submitting review:", error)
      toast.error("Failed to submit review")
    } finally {
      setSubmitting(false)
    }
  }

  const StarRating = ({
    value,
    onChange,
    label,
  }: { value: number; onChange: (rating: number) => void; label: string }) => (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <Label className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</Label>
        <span className="text-[10px] font-bold text-blue-500 uppercase tracking-widest">{value > 0 ? `${value}/5` : "Tap to rate"}</span>
      </div>
      <div className="flex items-center gap-1 bg-white dark:bg-slate-900 p-3 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm w-fit">
        {[1, 2, 3, 4, 5].map((rating) => (
          <button
            key={rating}
            type="button"
            onClick={() => onChange(rating)}
            className="p-1 hover:scale-110 transition-transform active:scale-90"
          >
            <Star
              className={`w-6 h-6 ${
                rating <= value ? "fill-yellow-400 text-yellow-400" : "text-slate-100 dark:text-slate-800"
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  )

  const currentCoreAttributes = coreAttributeFilters.filter((attr) => attr.activityTypes.includes(activityType || ""))
  const currentAdditionalCharacteristics = additionalCharacteristics.filter((attr) =>
    attr.activityTypes.includes(activityType || ""),
  )

  const currentFilters =
    activityType === "surfing"
      ? [...surfingFilters, ...universalFilters]
      : activityType === "kitesurfing"
        ? [...kitesurfingFilters, ...universalFilters]
        : []

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="space-y-8">
        {/* Activity Type Selection */}
        <div className="space-y-3">
          <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1 block">What activity did you do? *</Label>
          <div className="grid grid-cols-2 gap-3">
            {activityTypes.map((activity) => (
              <div
                key={activity.value}
                onClick={() => { triggerHaptic('selection'); setActivityType(activity.value); }}
                className={`flex flex-col items-center gap-2 p-4 border rounded-2xl cursor-pointer transition-all active:scale-[0.95] ${
                  activityType === activity.value
                    ? "border-blue-500 bg-blue-50/50 dark:bg-blue-900/20 text-blue-600"
                    : "border-slate-100 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-950/30 text-slate-400"
                }`}
              >
                <span className="text-2xl">{activity.emoji}</span>
                <span className="text-[10px] font-black uppercase tracking-wider">{activity.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Overall Rating */}
        {activityType && (
          <div className="space-y-3">
            <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1 block">Overall Experience *</Label>
            <div className="bg-slate-50 dark:bg-slate-950 rounded-[2rem] p-6 border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col items-center justify-center gap-4">
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    type="button"
                    onClick={() => handleOverallRatingChange(rating)}
                    className="p-1 hover:scale-110 transition-transform active:scale-90"
                  >
                    <Star
                      className={`w-10 h-10 ${
                        rating <= overallRating ? "fill-yellow-400 text-yellow-400" : "text-slate-200 dark:text-slate-800"
                      }`}
                    />
                  </button>
                ))}
              </div>
              <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                {overallRating === 0 ? "Tap to rate" : `${overallRating} / 5 Stars`}
              </span>
            </div>
          </div>
        )}

        {/* Unsplash Image Picker */}
        {activityType && spotLocation && !reviewId && (
          <div className="space-y-3">
            <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1 block">Spot Photo</Label>
            <div className="bg-slate-50 dark:bg-slate-950 rounded-[2rem] p-6 border border-slate-100 dark:border-slate-800 shadow-sm">
              <UnsplashImagePicker
                locationName={spotLocation}
                spotId={surfSpotId}
                currentUserId={user?.id}
                onImageSelect={setSelectedImage}
                selectedImageId={selectedImage?.unsplashId}
                refreshTrigger={imagePickerRefreshTrigger}
              />
            </div>
          </div>
        )}

        {/* Detailed Ratings */}
        {activityType && (
          <div className="space-y-3">
            <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1 block">Detailed Ratings</Label>
            <div className="bg-slate-50 dark:bg-slate-950 rounded-[2rem] p-6 border border-slate-100 dark:border-slate-800 shadow-sm space-y-6">
              <StarRating
                label="Wave Quality"
                value={ratings.wave_quality}
                onChange={(rating) => handleRatingChange("wave_quality", rating)}
              />
              <StarRating
                label="Crowd Level (1=Empty, 5=Packed)"
                value={ratings.crowd_level}
                onChange={(rating) => handleRatingChange("crowd_level", rating)}
              />
              <StarRating
                label="Water Quality"
                value={ratings.water_quality}
                onChange={(rating) => handleRatingChange("water_quality", rating)}
              />
              <StarRating
                label="Amenities"
                value={ratings.amenities_rating}
                onChange={(rating) => handleRatingChange("amenities_rating", rating)}
              />
            </div>
          </div>
        )}

        {/* Core Spot Attributes */}
        {activityType && currentCoreAttributes.length > 0 && (
          <div className="space-y-6">
            <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1 block border-b border-slate-100 dark:border-slate-800 pb-2">Technical Details</Label>
            {currentCoreAttributes.map((attrGroup) => (
              <div key={attrGroup.category} className="space-y-3">
                <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 flex items-center gap-2">
                  <attrGroup.icon className="w-3.5 h-3.5" />
                  {attrGroup.category}
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {attrGroup.options.map((option) => (
                    <div
                      key={option.value}
                      onClick={() => handleCoreAttributeSelect(attrGroup.field, option.value)}
                      className={`flex items-center justify-center gap-2 p-3 border rounded-xl cursor-pointer transition-all active:scale-[0.98] ${
                        coreAttributes[attrGroup.field as keyof typeof coreAttributes] === option.value
                          ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-600"
                          : "border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
                      }`}
                    >
                      <span className="text-sm">{option.emoji}</span>
                      <span className="text-[9px] font-black uppercase tracking-widest truncate">{option.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Activity-Specific and Universal Filters */}
        {activityType && (
          <div className="space-y-6">
            <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1 block border-b border-slate-100 dark:border-slate-800 pb-2">Conditions & Features</Label>
            {currentFilters.map((filterGroup) => (
              <div key={filterGroup.category} className="space-y-3">
                <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1 flex items-center gap-2">
                  <filterGroup.icon className="w-3.5 h-3.5" />
                  {filterGroup.category}
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {filterGroup.options.map((option) => (
                    <div
                      key={option.value}
                      onClick={() => handleFilterToggle(option.value)}
                      className={`flex items-center justify-center gap-2 p-3 border rounded-xl cursor-pointer transition-all active:scale-[0.98] ${
                        selectedFilters.includes(option.value)
                          ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-600"
                          : "border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
                      }`}
                    >
                      <span className="text-sm">{option.emoji}</span>
                      <span className="text-[9px] font-black uppercase tracking-widest truncate">{option.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Comment */}
        {activityType && (
          <div className="space-y-3">
            <Label htmlFor="comment" className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Your Review (Optional)</Label>
            <Textarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={`Share your ${activityType} experience at this spot...`}
              className="rounded-[2rem] border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 focus:ring-blue-500/20 min-h-[120px] py-6 px-6 shadow-sm"
              rows={4}
            />
          </div>
        )}
      </div>

      <div className="pt-6 mt-6 border-t border-slate-100 dark:border-slate-800">
        {(!activityType || overallRating === 0) && (
          <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/30 rounded-2xl flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/20 flex items-center justify-center shrink-0">
              <Sparkles className="w-4 h-4 text-amber-600" />
            </div>
            <p className="text-[10px] font-bold text-amber-700 dark:text-amber-400 leading-tight">
              {!activityType ? "Please select an activity type." : "Please provide an overall rating."}
            </p>
          </div>
        )}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button 
            type="submit" 
            disabled={submitting || !activityType || overallRating === 0}
            className="h-14 flex-[2] rounded-[1.25rem] bg-blue-600 hover:bg-blue-700 text-white font-black uppercase tracking-widest text-xs shadow-xl shadow-blue-500/25 active:scale-95 transition-all"
          >
            {submitting ? "Submitting..." : reviewId ? "Update Review" : "Submit Review"}
          </Button>
          {onCancel && (
            <Button 
              type="button" 
              variant="outline" 
              onClick={onCancel}
              className="h-14 flex-1 rounded-[1.25rem] font-bold text-slate-500 border-slate-100 dark:border-slate-800"
            >
              Cancel
            </Button>
          )}
        </div>
      </div>
    </form>
  )
}

export default EnhancedReviewForm
